import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  UploadCloud, FileSpreadsheet, CheckCircle2, RefreshCw, Cloud, ArrowRight,
} from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/date";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const FIELD_LABELS = {
  customer_name: "Customer Name *",
  customer_email: "Email",
  invoice_no: "Invoice No *",
  invoice_date: "Invoice Date",
  due_date: "Due Date",
  amount: "Amount / Balance *",
  currency: "Currency",
  payment_terms: "Payment Terms (days)",
};

function Dropzone({ source, testid }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const inputRef = useRef();

  const onFile = async (f) => {
    if (!f) return;
    setFile(f);
    setDone(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/import/preview", fd);
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      toast.success(`Detected ${data.headers.length} columns, ${data.rows} rows`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read file");
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", source);
      fd.append("mapping", JSON.stringify(mapping));
      const { data } = await api.post("/import/commit", fd);
      setDone(data);
      toast.success(`Imported ${data.imported} invoices (${data.customers} customers)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setFile(null); setPreview(null); setMapping({}); setDone(null); };

  return (
    <Card className="p-5 lg:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-medium capitalize">{source} file</h3>
        </div>
        <Badge variant="outline" className={source === "domestic"
          ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20"
          : "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20"}>
          {source === "domestic" ? "INR" : "USD/EUR"}
        </Badge>
      </div>

      {!file && (
        <div
          data-testid={testid}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
          className="border-2 border-dashed border-border rounded-xl p-10 text-center cursor-pointer hover:border-primary hover:bg-muted/40 transition-colors"
        >
          <UploadCloud className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm font-medium">Drop {source} Excel file here</p>
          <p className="text-xs text-muted-foreground mt-1">or click to browse (.xlsx, .xls)</p>
          <input ref={inputRef} type="file" accept=".xlsx,.xls" hidden
                 onChange={(e) => onFile(e.target.files[0])} />
        </div>
      )}

      {file && done && (
        <div className="text-center py-6">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
          <p className="font-medium">Imported {done.imported} invoices</p>
          <p className="text-xs text-muted-foreground mt-1">{done.customers} customers · {done.skipped} rows skipped</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={reset}>Import another</Button>
        </div>
      )}

      {file && preview && !done && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm">
            <FileSpreadsheet className="w-4 h-4 text-emerald-500" />
            <span className="font-medium truncate">{file.name}</span>
            <span className="text-muted-foreground">· {preview.rows} rows</span>
          </div>
          <p className="text-xs text-muted-foreground">Map your columns to fields. Fields marked * are required.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {preview.fields.map((field) => (
              <div key={field} className="space-y-1">
                <Label className="text-xs">{FIELD_LABELS[field]}</Label>
                <Select value={mapping[field] || "__none__"}
                        onValueChange={(v) => setMapping((m) => ({ ...m, [field]: v === "__none__" ? undefined : v }))}>
                  <SelectTrigger data-testid={`map-${source}-${field}`}><SelectValue placeholder="Select column" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— none —</SelectItem>
                    {preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button onClick={commit} disabled={busy} data-testid={`column-mapping-save-button-${source}`}>
              {busy ? "Importing…" : <>Import <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
            <Button variant="ghost" onClick={reset}>Cancel</Button>
          </div>
        </div>
      )}

      {busy && !preview && <p className="text-sm text-muted-foreground py-4">Reading file…</p>}
    </Card>
  );
}

function DropboxPanel() {
  const [cfg, setCfg] = useState(null);
  const [domestic, setDomestic] = useState("");
  const [exportUrl, setExportUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/sync/config");
    setCfg(data);
    setDomestic(data.domestic_url || "");
    setExportUrl(data.export_url || "");
  };
  useEffect(() => { load(); }, []);

  const saveLinks = async () => {
    setSaving(true);
    try {
      await api.put("/sync/config", { domestic_url: domestic, export_url: exportUrl });
      toast.success("Dropbox links saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save links (admin only)");
    } finally {
      setSaving(false);
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sync/run");
      if (data.status === "skipped") toast.warning(data.message);
      else {
        const parts = Object.entries(data.summary || {}).map(([s, r]) => `${s}: ${r.imported} invoices`);
        toast.success(`Sync complete — ${parts.join(", ") || "no files"}`);
        if (data.errors && Object.keys(data.errors).length)
          toast.error(`Some files failed: ${Object.keys(data.errors).join(", ")}`);
      }
      load();
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const configured = domestic || exportUrl;

  return (
    <Card className="p-5 lg:p-6" data-testid="dropbox-sync-panel">
      <div className="flex items-center gap-2 mb-2">
        <Cloud className="w-5 h-5 text-primary" />
        <h3 className="font-heading text-lg font-medium">Dropbox Auto-Sync</h3>
        <Badge variant="outline" className={configured
          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
          : "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"}>
          {configured ? "Configured" : "Not configured"}
        </Badge>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Paste your Dropbox share links. The latest copy is pulled automatically <strong>every day</strong> and
        whenever you click Sync now. Existing invoices are updated by invoice number; your customer master and
        exclusions are kept.
      </p>
      <div className="grid grid-cols-1 gap-3 mb-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Domestic file link</Label>
          <Input value={domestic} onChange={(e) => setDomestic(e.target.value)}
                 placeholder="https://www.dropbox.com/scl/fi/.../Domestic.xlsm?...&dl=0"
                 data-testid="dropbox-domestic-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Export file link</Label>
          <Input value={exportUrl} onChange={(e) => setExportUrl(e.target.value)}
                 placeholder="https://www.dropbox.com/scl/fi/.../Export.xlsm?...&dl=0"
                 data-testid="dropbox-export-input" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={saveLinks} disabled={saving} data-testid="dropbox-save-button">
          {saving ? "Saving…" : "Save links"}
        </Button>
        <Button onClick={run} disabled={busy || !configured} data-testid="dropbox-sync-trigger-button">
          <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} /> Sync now
        </Button>
        {cfg?.last_sync && (
          <span className="text-xs text-muted-foreground">Last sync: {fmtDateTime(cfg.last_sync)}</span>
        )}
      </div>
      {cfg?.summary && Object.keys(cfg.summary).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {Object.entries(cfg.summary).map(([s, r]) => (
            <Badge key={s} variant="outline" className="text-xs capitalize">
              {s}: {r.imported} imported · {r.skipped} skipped · {r.customers} customers{r.marked_paid ? ` · ${r.marked_paid} marked paid` : ""}
            </Badge>
          ))}
        </div>
      )}
      {cfg?.errors && Object.keys(cfg.errors).length > 0 && (
        <p className="text-xs text-rose-500 mt-2">Errors: {Object.entries(cfg.errors).map(([s, e]) => `${s}: ${e}`).join(" | ")}</p>
      )}
    </Card>
  );
}

export default function Import() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Import Receivables</h1>
        <p className="text-muted-foreground mt-1 text-sm">Auto-sync from Dropbox, or upload files manually and map the columns.</p>
      </div>
      <DropboxPanel />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <Dropzone source="domestic" testid="excel-upload-dropzone-domestic" />
        <Dropzone source="export" testid="excel-upload-dropzone-export" />
      </div>
    </div>
  );
}
